import { LoadingState } from "@repo/app-types/client/client";
import type { ApiKey } from "@repo/app-types/entities/apiKey";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Link } from "@repo/ui/Link/index";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { TableCard } from "../../../components/layout/pageLayout.js";
import { PH_MASK_CLASS } from "../../analytics/sessionRecordingMasking.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";
import { CreateApiKeyModal } from "./createApiKeyModal.js";
import { DeleteApiKeyModal } from "./deleteApiKeyModal.js";
import { SecretsSection } from "./secretsSection.js";

export const DeveloperSettingsSection: FC = () => {
  const [listApiKeysLoadingState, setListApiKeysLoadingState] =
    useState<LoadingState>(LoadingState.LOADING);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKey | null>(null);
  const [isCreateApiKeyModalOpen, setIsCreateApiKeyModalOpen] = useState(false);

  const fetchApiKeys = async () => {
    setListApiKeysLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.get(`/api-keys`);

      setApiKeys(data);
      setListApiKeysLoadingState(LoadingState.LOADED);
    } catch (err) {
      // EntityTable shows its error only when it has no rows, and keys from
      // before a failed refetch may have been deleted since.
      setApiKeys([]);
      setListApiKeysLoadingState(LoadingState.ERROR);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  // Laid out like the secrets table below it.
  const columns = useMemo(
    (): EntityTableColumnDefinition<ApiKey>[] => [
      {
        id: "apiKey",
        header: "API key",
        isRowHeader: true,
        getTextValue: ({ name }) => name,
        renderCell: (apiKey) => (
          <div className="flex">
            {/* `w-0 flex-auto` keeps the nowrap text out of the table's width, so it truncates. */}
            <div className="w-0 flex-auto">
              <div className="truncate text-sm font-medium text-foreground">
                {apiKey.name}
              </div>
              <div
                className={twMerge(
                  "font-mono text-xs text-muted-foreground",
                  PH_MASK_CLASS,
                )}
              >
                ••••{apiKey.key_suffix}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "createdAt",
        header: "Date created",
        className: "w-0 whitespace-nowrap",
        renderCell: (apiKey) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {DateTime.fromISO(apiKey.created_at).toLocaleString(
              DateTime.DATETIME_MED,
            )}
          </span>
        ),
      },
      {
        id: "actions",
        header: <span className="sr-only">Actions</span>,
        className: "w-0",
        // `group-[:hover]`, as in the secrets table: `group-hover` never
        // matches in EntityTable rows.
        renderCell: (apiKey) => (
          <div className="invisible flex items-center gap-1 group-[:focus-within]/row:visible group-[:hover]/row:visible">
            <IconButton
              icon={<IconTrash className="h-4 w-4" />}
              variant={ButtonVariant.Secondary}
              accessibleLabel={`Delete ${apiKey.name}`}
              onClick={() => setApiKeyToDelete(apiKey)}
            />
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <SettingsSectionPage title={settingsSections.developers.label}>
      <div className="flex flex-col gap-8">
        <section className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-foreground">
              API keys
            </h2>
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              icon={<IconPlus className="h-4 w-4" />}
              onClick={() => {
                setIsCreateApiKeyModalOpen(true);
              }}
            >
              Create API key
            </Button>
          </div>
          <TableCard>
            <EntityTable
              aria-label="API keys"
              columns={columns}
              entities={apiKeys}
              isLoading={listApiKeysLoadingState === LoadingState.LOADING}
              isError={listApiKeysLoadingState === LoadingState.ERROR}
              emptyMessage="Create an API key to use the Alpharun API."
              size={Size.SMALL}
              isFullBleed
            />
          </TableCard>
          <div className="text-sm text-muted-foreground">
            API keys allow you to use the Alpharun API.{" "}
            <Link href="https://help.alpharun.com/en/articles/2476993">
              View API documentation here
            </Link>
            {"."}
          </div>
        </section>
        <SecretsSection />
      </div>
      <CreateApiKeyModal
        isOpen={isCreateApiKeyModalOpen}
        onClose={() => {
          setIsCreateApiKeyModalOpen(false);
          fetchApiKeys();
        }}
      />
      <DeleteApiKeyModal
        isOpen={Boolean(apiKeyToDelete)}
        onClose={() => {
          setApiKeyToDelete(null);
          fetchApiKeys();
        }}
        apiKeyToDelete={apiKeyToDelete}
      />
    </SettingsSectionPage>
  );
};
